/**
 * useAppVersionWatcher
 *
 * 🔄 2026-05-01 — Détecte un redéploiement backend en arrière-plan et propose
 * un reload à l'utilisateur via un toast persistant cliquable.
 *
 * 🔧 2026-05-XX — Option B : comparaison hash-vs-hash
 *  - Le backend expose `version_id` = contenu de /app/VERSION (hash git court)
 *  - Le frontend compare avec `process.env.REACT_APP_VERSION` (baked au build CRACO)
 *  - Un simple redémarrage de pod backend NE déclenche PLUS de reload côté client
 *    (fix du bug "auto-reload toutes les 5 min")
 *
 * 🛡️  [Phase 57.6 — 2026-06-14] Toast persistant + purge ACTIVE au clic
 *  - Bug récurrent : l'utilisatrice voit l'ancienne version après un déploiement
 *    car le SW / cache PWA / Cloudflare sert encore le vieux bundle.
 *  - Le reload AUTO silencieux pouvait surprendre / faire perdre du travail.
 *  - Solution : afficher un TOAST PERSISTANT (`duration: Infinity`) avec un
 *    bouton "Recharger" qui :
 *      1. Désinscrit TOUS les service workers
 *      2. Vide TOUTES les caches (Cache Storage + IndexedDB)
 *      3. Force un reload avec cache-buster `?v=TIMESTAMP`
 *    L'utilisatrice CONSCIEMMENT déclenche le reload → zéro surprise.
 *
 * Stratégie :
 *  - Au montage : si `REACT_APP_VERSION` absent/vide → désactiver silencieusement
 *  - Toutes les 60s : GET /api/version, compare data.version_id avec REACT_APP_VERSION
 *  - À l'event "online" / focus : check immédiat
 *  - Si mismatch → afficher le toast UNE SEULE fois (anti-duplication)
 *
 * IMPORTANT : Le reload n'efface PAS les punchs en cours (en DB) ni les tokens JWT.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BAKED_VERSION = (process.env.REACT_APP_VERSION || '').trim();
const POLL_INTERVAL_MS = 60_000; // 1 min
const TOAST_ID = 'app-version-update-available'; // Anti-duplication

/**
 * 🛡️ [Phase 57.6] Purge ACTIVE du cache PWA + Service Worker + reload forcé.
 * Exporté pour pouvoir être appelé depuis un bouton "Forcer rafraîchissement"
 * dans la page Paramètres.
 */
export async function purgeCachesAndReload() {
  try {
    // 1. Désinscrire TOUS les service workers
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    // 2. Vider TOUTES les caches (Cache Storage)
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // 3. Vider les flags de purge précédente pour éviter les blocages
    try {
      sessionStorage.removeItem('__prqc_cache_purged__');
      sessionStorage.removeItem('last_version_reload_at');
      localStorage.removeItem('version_watcher_kill_switch');
      localStorage.removeItem('version_watcher_dead');
    } catch {
      // Storage indisponible → on continue
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[version-watcher] purge partielle :', err);
  }

  // 4. Force un reload avec cache-buster ?v=TIMESTAMP
  if (typeof window !== 'undefined') {
    const cleanSearch = window.location.search
      .replace(/[?&]v=\d+/g, '')
      .replace(/^&/, '?');
    const newSearch = cleanSearch
      ? `${cleanSearch}&v=${Date.now()}`
      : `?v=${Date.now()}`;
    window.location.replace(window.location.pathname + newSearch + window.location.hash);
  }
}

export function useAppVersionWatcher() {
  const intervalRef = useRef(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    // Si pas de version baked au build → watcher désactivé (dev non configuré, etc.)
    if (!BAKED_VERSION) {
      // eslint-disable-next-line no-console
      console.info('[version-watcher] disabled (no REACT_APP_VERSION at build time)');
      return undefined;
    }

    let mounted = true;

    const showUpdateToast = (serverVersion) => {
      if (toastShownRef.current) return; // Anti-duplication
      toastShownRef.current = true;
      toast.message('🔄 Nouvelle version disponible', {
        id: TOAST_ID,
        duration: Infinity, // ⚠️ Persistant — ne disparaît pas tout seul
        description: `Cliquez sur Recharger pour appliquer les dernières mises à jour.`,
        action: {
          label: 'Recharger',
          onClick: () => {
            // eslint-disable-next-line no-console
            console.info(
              `[version-watcher] reload demandé par utilisateur: ` +
              `client=${BAKED_VERSION} server=${serverVersion}`
            );
            // Petit délai pour que le toast disparaisse avant le reload
            setTimeout(() => { purgeCachesAndReload(); }, 100);
          },
        },
        // dismissible=false évite que l'utilisateur ferme accidentellement
        // (Sonner v1.x : option `dismissible: false`)
        dismissible: false,
      });
    };

    const fetchVersion = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/version?_=${Date.now()}`, {
          timeout: 8000,
        });
        const serverVersion = res?.data?.version_id;
        if (!serverVersion || !mounted) return;

        // Comparaison stricte hash-vs-hash
        if (String(serverVersion) !== BAKED_VERSION) {
          // 🛡️ [Phase 57.6] Plus de reload silencieux : on affiche un toast
          // persistant. L'utilisatrice clique consciemment pour recharger.
          showUpdateToast(serverVersion);
        }
      } catch {
        // Silencieux — peut être hors-ligne, pas grave
      }
    };

    // Premier check immédiat
    fetchVersion();
    intervalRef.current = setInterval(fetchVersion, POLL_INTERVAL_MS);

    const onFocus = () => fetchVersion();
    const onOnline = () => fetchVersion();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, []);
}

export default useAppVersionWatcher;
